import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputLabel,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LocalizationProvider, StaticDatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { useGetTypicalMonthsQuery } from 'state/apiSlice';
import { useAppSelector } from 'state/hooks';
import {
  selectCurrentProjectEnd,
  selectSpecialEventDates,
} from 'state/userAdminSlices';
import { getUniqueDateMonthPairs } from 'state/utils';
import { selectProject } from 'state/workflowSlice';

import { MAX_CHARS } from 'appConstants';
import { SpecialDateRequest, YearMonth } from 'appTypes';
import { sortTypicalMonths } from 'appUtils';
import dayjs from 'dayjs';
import { ConfirmCreatePopup } from 'features/common/ConfirmCreatePopup';
import DateRangePicker from 'features/common/DateTimeSelector/DateRangePicker';
import DateTimePickerWrapper from 'features/common/DateTimeSelector/DateTimePickerWrapper';
import { MonthYearMenu } from 'features/common/DateTimeSelector/MonthYearMenu';
import { buildYearMonth } from 'features/common/DateTimeSelector/utils';
import FontAwesomeIcon from 'features/common/FontAwesomeIcon';
import { StlButton } from 'features/common/StlButton/StlButton';
import StlConfirmButtons from 'features/common/StlConfirmButtons';
import StlPopoverWrapper from 'features/common/StlPopoverWrapper/StlPopoverWrapper';
import { StlPopup } from 'features/common/StlPopup/StlPopup';
import {
  getLast24Months,
  parseDate,
  validateDateRange,
} from 'features/common/datetimeUtils';
import { useSubscription } from 'features/common/hooks/useSubscription';
import { classNames } from 'features/common/utils';
import { dimmed_white_color } from 'theme/cemTheme';

import { formatDateShort } from '../common';
import { useUprStatus } from '../hooks';
import { useDatesQuota } from './hooks';
import {
  formatDateToString,
  generateDateStringsFromRange,
  getMinDate,
} from './utils';

import datePickerStyles from '../../task_bar/DatePicker.module.css';
import styles from './SpecialDateSettings.module.css';

interface ISpecialDateSetting {
  open: boolean;
  onClose: () => void;
  onSave: (subId: string, request: SpecialDateRequest) => void;
  initialValue?: SpecialDateRequest; // undefined = creating
  subscriptionId?: string;
  projectId?: string;
}

const LAST_24_MONTHS = getLast24Months();
const initialState: SpecialDateRequest = {
  comparison_month: null,
  name: ``,
  dates: [], // '2025-01-01'
  date_range_end: null,
  date_range_start: null,
};

const buttonSx = {
  background: 'var(--background-input-dark) !important',
  border: '1px solid #4D4D4D !important',
  height: '32px',
};

export default function SpecialDateSetting({
  open,
  onClose,
  initialValue,
  onSave,
  subscriptionId,
  projectId,
}: ISpecialDateSetting) {
  const { sub_id } = useParams();
  const { subscription, project_data } = useSubscription(
    subscriptionId,
    projectId,
  );
  const projectUprEnd = useAppSelector(selectCurrentProjectEnd);
  const uprDates = useAppSelector(selectSpecialEventDates);
  const specialDates = project_data?.special_date_requests ?? uprDates;
  const [state, setState] = useState<SpecialDateRequest>(
    initialValue ?? initialState,
  );
  const [useRange, setUseRange] = useState<boolean>(
    Boolean(initialValue?.date_range_start || initialValue?.date_range_end),
  );
  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [datesError, setDatesError] = useState<string | undefined>(undefined);
  const [openDates, setOpenDates] = useState<boolean>(false);
  const [openComp, setOpenComp] = useState<boolean>(false);
  const [titleComp, setTitleComp] = useState<string>('');
  const [customName, setCustomName] = useState<boolean>(false);
  const [tempUsedQuota, setTempUsedQuota] = useState<number>(0);
  const [freeQuotaForEdit, setFreeQuotaForEdit] = useState<number>(0);
  const [tempRange, setTempRange] =
    useState<Partial<SpecialDateRequest>>(undefined);
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);

  const { freeQuota, usedQuota, allQuota } = useDatesQuota(
    subscriptionId,
    projectId,
  );
  const { isEditable, isNew } = useUprStatus();
  const isUpr = isNew || isEditable;
  const isEditMode =
    isEditable && Boolean(initialValue?.tempId || initialValue?.hash_id);

  const projectEndDate = isUpr ? projectUprEnd : project_data?.project_end;
  const defaultDates = useMemo((): [dayjs.Dayjs, dayjs.Dayjs] => {
    const endRaw = projectEndDate;
    const today = dayjs();
    const yesterday = today.subtract(1, 'day');
    const end = endRaw ? parseDate(endRaw) : today;

    let to: dayjs.Dayjs;
    let from: dayjs.Dayjs;
    if (end < today) {
      to = end;
      from = end.subtract(1, 'day');
    } else {
      to = today;
      from = yesterday;
    }
    return [from, to];
  }, [projectEndDate]);

  useEffect(() => {
    if (!open) return;

    if (initialValue) {
      setState(initialValue);
      setUseRange(
        Boolean(initialValue.date_range_start || initialValue.date_range_end),
      );
      setCustomName(true);
    } else {
      const [_, to] = defaultDates;
      setUseRange(false);

      setState((prev) => ({
        ...prev,
        // comment out for empty defaults version
        dates: [formatDateToString(to)],
      }));
      setCustomName(false);
    }

    setNameError(undefined);
    setDatesError(undefined);
  }, [open, initialValue, defaultDates]);

  // sync titleComparison = comparison_month
  useEffect(() => {
    if (state.comparison_month)
      setTitleComp(dayjs(`${state.comparison_month}-01`).format('MMMM, YYYY'));
    else setTitleComp('');
  }, [state.comparison_month]);

  // auto-generate name from dates unless user provided custom name or editing
  useEffect(() => {
    if (customName || isEditMode) return;
    if (state.dates?.length > 0) {
      const from = state.dates[0];
      const to = state.dates.at(-1);
      let name: string;
      if (to === from)
        name = `Event ${formatDateShort(from)}`; // single date
      else name = `Event ${formatDateShort(from)} - ${formatDateShort(to)}`; // range
      // validate uniquness - edge case
      let i = 1;
      while (existing_names.includes(name)) {
        const base = name.split(' (')[0];
        name = `${base} (${i})`;
        i += 1;
      }

      setState((prev) => ({ ...prev, name }));
    } else setState((prev) => ({ ...prev, name: '' }));
  }, [state?.dates, isEditMode]);

  const firstDate = state.dates?.[0];

  const computedComparison = useMemo(() => {
    if (!firstDate) return null;
    // don't run on the first render when editing an existing value
    if (isEditMode && initialValue?.dates?.[0] === firstDate) return null;
    // dayjs months are 0-based; buildYearMonth likely expects 1-12
    const prevMonth = dayjs(firstDate).subtract(1, 'month');
    return buildYearMonth(prevMonth.year(), prevMonth.month() + 1);
  }, [firstDate, isEditMode, initialValue?.dates?.[0]]);

  useEffect(() => {
    if (!computedComparison) return;
    setState((prev) =>
      prev.comparison_month === computedComparison
        ? prev
        : { ...prev, comparison_month: computedComparison },
    );
  }, [computedComparison]);

  // calculate temporary quota usage when editing or selecting a range
  useEffect(() => {
    if (!useRange) return;
    let effectiveDates: SpecialDateRequest[];
    let remainingDates: SpecialDateRequest[];
    // edit unsaved on BE
    if (isEditMode) {
      if (initialState.hash_id) {
        effectiveDates = specialDates.map((req) =>
          req.hash_id === initialValue.hash_id
            ? { ...state, ...tempRange }
            : req,
        );
        remainingDates = specialDates.filter(
          (el) => el.hash_id !== initialValue.hash_id,
        );
      } else if (initialValue.tempId) {
        effectiveDates = specialDates.map((req) =>
          req.tempId === initialValue.tempId ? { ...state, ...tempRange } : req,
        );
        remainingDates = specialDates.filter(
          (el) => el.tempId !== initialValue.tempId,
        );
      }

      setFreeQuotaForEdit(
        allQuota - getUniqueDateMonthPairs(remainingDates).size,
      );
    } else effectiveDates = [...specialDates, { ...state, ...tempRange }];

    const pairs = getUniqueDateMonthPairs(effectiveDates);

    setTempUsedQuota(pairs.size);
  }, [tempRange, specialDates, isEditMode, allQuota, openDates]);

  const existing_names = useMemo(() => {
    if (!specialDates) return [];
    if (!initialValue?.tempId) return specialDates.map((el) => el.name); // new

    // exclude the one being edited
    return specialDates
      .filter(
        (el) =>
          el.tempId !== initialValue.tempId &&
          el.hash_id !== initialValue.hash_id,
      )
      .map((el) => el.name);
  }, [specialDates, initialValue?.tempId, initialValue?.hash_id]);

  const handleComparisonChange = (date: YearMonth) => {
    setState((prev) => ({ ...prev, comparison_month: date }));
  };

  const handleNameChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { value } = e.target;

    if (value && !customName) setCustomName(true);
    validateName(value.trim());
    setState((prev) => ({
      ...prev,
      name: value,
    }));
  };

  const validateName = (value: string) => {
    if (value?.length > MAX_CHARS)
      setNameError(`Maximum ${MAX_CHARS} characters`);
    else if (value && existing_names.includes(value)) {
      return setNameError(
        'A Special Event Date of this name already exists in your project',
      );
    } else return setNameError(undefined);
  };

  const handleApply = () => {
    onSave(sub_id, { ...state, name: state.name.trim() });
    if (!isEditMode) {
      setOpenConfirm(false);
      onClose();
    }
  };

  const handleCancel = () => {
    onClose();
    setState(initialState);
  };

  const getButtonTitle = () => {
    let rangeStart = state?.date_range_start;
    let rangeEnd = state?.date_range_end;
    if (state?.dates.length > 0 && !useRange)
      return formatDateShort(state.dates[0]);
    if (tempRange) {
      rangeStart = tempRange.date_range_start;
      rangeEnd = tempRange.date_range_end;
    }
    if (rangeStart || rangeEnd)
      return `${formatDateShort(rangeStart)} - ${rangeEnd ? formatDateShort(rangeEnd) : ''}`;
    return '';
  };

  const applyRange = () => {
    if (!tempRange) return;
    const { date_range_start, date_range_end, dates } = tempRange;
    setState((prev) => ({
      ...prev,
      date_range_end,
      date_range_start,
      dates,
    }));
    setOpenDates(false);
    setTempRange(undefined);
  };

  const updateRangeTemp = (dates: [dayjs.Dayjs | null, dayjs.Dayjs | null]) => {
    const [start, end] = dates;
    const rangeData: Partial<SpecialDateRequest> = {
      date_range_start: start ? formatDateToString(start) : null,
      date_range_end: end ? formatDateToString(end) : null,
      dates: generateDateStringsFromRange(start, end),
    };
    setTempRange(rangeData);
  };

  const validateDate = (value: dayjs.Dayjs) => {
    if (!value) return setDatesError('Date must be set');
    if (projectEndDate && value > dayjs(projectEndDate))
      return setDatesError(`Date must be before ${projectEndDate}`);
    return setDatesError(undefined);
  };

  const setDate = (value: dayjs.Dayjs) => {
    validateDate(value);
    setState((prev) => ({
      ...prev,
      dates: [formatDateToString(value)],
    }));
  };

  const handleRangeCheckChange = (event) => {
    const shouldUseRange = event.target.checked;
    setUseRange(shouldUseRange);
    // version empty defaults
    // setState({
    //   ...state,
    //   dates: [],
    //   date_range_start: null,
    //   date_range_end: null,
    // });

    // if switching to range and no existing range -> prefill from defaults
    let [from, to] = defaultDates;
    if (shouldUseRange) {
      if (!state.date_range_start || !state.date_range_end) {
        if (state.dates && state.dates.length > 0) {
          from = parseDate(state.dates[0]);
          to = from;
        }
        setState((prev) => ({
          ...prev,
          dates: generateDateStringsFromRange(from, to),
          date_range_start: formatDateToString(from),
          date_range_end: formatDateToString(to),
        }));
      }
    } else if (
      state.date_range_start ||
      state.date_range_end ||
      !state.dates ||
      state.dates.length === 0
    )
      setState((prev) => ({
        ...prev,
        date_range_start: null,
        date_range_end: null,
        dates: [state.date_range_start],
      }));
  };

  const cancelRange = () => {
    setOpenDates(false);
    setTempRange(undefined);
  };

  const dateRangeValid = validateDateRange(
    state.date_range_start,
    state.date_range_end,
  );

  const tempRangeValid = validateDateRange(
    tempRange?.date_range_start,
    tempRange?.date_range_end,
  );

  const [key, setKey] = useState(0);

  const resetRange = () => {
    setTempRange(undefined);
    setKey((k) => k + 1);
    // version empty defaults
    // setState({
    //   ...state,
    //   dates: [],
    //   date_range_end: null,
    //   date_range_start: null,
    // });
  };
  const threeYearsFromNowDayjs = useMemo(() => dayjs().add(3, 'year'), []);

  const maxMonthYear = useMemo(() => {
    const projectEnd = projectEndDate ? dayjs(projectEndDate) : null;

    const maxDate =
      projectEnd && projectEnd.isBefore(threeYearsFromNowDayjs)
        ? projectEnd.subtract(1, 'month')
        : threeYearsFromNowDayjs;

    return maxDate.format('YYYY-MM') as YearMonth;
  }, [projectEndDate, threeYearsFromNowDayjs]);

  const minMonthYear = LAST_24_MONTHS[LAST_24_MONTHS.length - 1];

  const maxDayjs = useMemo(() => {
    if (!projectEndDate) return threeYearsFromNowDayjs;

    const projectEnd = dayjs(projectEndDate);
    return projectEnd.isBefore(threeYearsFromNowDayjs)
      ? projectEnd
      : threeYearsFromNowDayjs;
  }, [projectEndDate, threeYearsFromNowDayjs]);

  const minDayjs = getMinDate();

  const quotaExceeded = tempUsedQuota > allQuota;
  const cannotSave =
    !state.name?.trim() ||
    !!nameError ||
    !!datesError ||
    quotaExceeded ||
    (useRange && !dateRangeValid);

  const title = isEditMode ? 'Edit Special Event' : 'Add Special Event';

  const usedQuotaInfo = useMemo(() => {
    let selected: number;
    let available: number;

    if (isEditMode) {
      selected = tempUsedQuota - (allQuota - freeQuotaForEdit);
      available = freeQuotaForEdit;
    } else {
      selected = tempUsedQuota - usedQuota;
      available = freeQuota;
    }
    return {
      selected,
      available,
      txt: `Uses ${selected} of ${available} quota days`,
    };
  }, [tempUsedQuota, freeQuotaForEdit, usedQuota, freeQuota, isEditMode]);

  return (
    <>
      <StlPopup
        btnTxt="Proceed"
        title={title}
        isOpen={open && !openConfirm}
        cancel
        closeOnApply={isEditMode}
        onCancel={handleCancel}
        onClose={handleCancel}
        onApply={isEditMode ? handleApply : () => setOpenConfirm(true)}
        disabled={cannotSave}
      >
        <Stack className={styles.popoverInner}>
          <Box className={styles.flexBox}>
            <InputLabel
              className={classNames(styles.leftChild, styles.label)}
              id="event-dates"
            >
              {useRange ? 'Date range' : 'Date of Event'}
            </InputLabel>
            <Stack className={styles.rightChild}>
              <StlPopoverWrapper
                buttonSx={buttonSx}
                open={openDates}
                setOpen={setOpenDates}
                endIcon={<FontAwesomeIcon name="calendar-day" />}
                renderLabel={getButtonTitle}
                onClose={() => setTempRange(undefined)}
              >
                <DateTimePickerWrapper
                  title="Select date"
                  width={useRange && 'auto'}
                  height={useRange && 'auto'}
                  subtitle={
                    useRange && (
                      <MaxDaysInfo
                        days={isEditMode ? freeQuotaForEdit : freeQuota}
                      />
                    )
                  }
                  component={
                    useRange ? (
                      <DateRangePicker
                        key={key}
                        // default date empty : openToDate={dayjs()}
                        startValue={parseDate(state.date_range_start)}
                        endValue={parseDate(state.date_range_end)}
                        minDate={minDayjs}
                        maxDate={maxDayjs}
                        onChange={updateRangeTemp}
                        maxDays={usedQuotaInfo?.available}
                      />
                    ) : (
                      <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                        adapterLocale="en"
                      >
                        <StaticDatePicker
                          className={classNames(
                            datePickerStyles.root,
                            styles.staticCalendar,
                          )}
                          value={parseDate(state.dates?.[0])}
                          minDate={minDayjs}
                          maxDate={maxDayjs}
                          reduceAnimations
                          onChange={setDate}
                          slotProps={{
                            day: {
                              onClick: () => setOpenDates(false),
                            },
                          }}
                        />
                      </LocalizationProvider>
                    )
                  }
                />
                {useRange && (
                  <Box display="flex" justifyContent="space-between">
                    <Box display="flex" justifyContent="flex-start" gap={0.25}>
                      <Typography
                        color={dimmed_white_color}
                        variant="body2"
                        fontSize="0.75rem"
                        alignContent="center"
                      >
                        {usedQuotaInfo.txt}
                      </Typography>

                      <StlButton
                        variant="text"
                        value="Reset"
                        width={52}
                        handleClick={resetRange}
                      />
                    </Box>
                    <StlConfirmButtons
                      width={64}
                      disabled={!tempRangeValid || quotaExceeded}
                      handleApply={applyRange}
                      handleCancel={cancelRange}
                    />
                  </Box>
                )}
              </StlPopoverWrapper>
              {datesError && (
                <FormHelperText error>{datesError}</FormHelperText>
              )}
              <FormGroup>
                <FormControlLabel
                  control={<Checkbox size="small" checked={useRange} />}
                  label={
                    <Typography variant="body2">Select a date range</Typography>
                  }
                  onChange={handleRangeCheckChange}
                />
              </FormGroup>
            </Stack>
          </Box>
          <Box className={styles.flexBox}>
            <InputLabel
              className={classNames(styles.leftChild, styles.label)}
              id="event-name"
            >
              Event Name
            </InputLabel>
            <TextField
              // default value is shown as a placeholder
              placeholder={!customName ? state.name : undefined}
              className={styles.rightChild}
              value={customName ? state.name : ''}
              name="name"
              onChange={handleNameChange}
              error={!!nameError}
              helperText={nameError}
              aria-labelledby="name"
              variant="outlined"
              required
              InputProps={{
                className: styles.textFieldRoot,
                inputProps: { maxLength: MAX_CHARS },
              }}
            />
          </Box>
          <Divider />

          <Box className={styles.flexBox}>
            <Stack className={styles.leftChild}>
              <InputLabel className={styles.label}>Comparison Month</InputLabel>
              <ComparisonDesc
                numb={
                  project_data?.num_months_rolling ??
                  subscription?.template?.num_months_rolling
                }
              />
            </Stack>
            <Stack className={styles.rightChild}>
              <StlPopoverWrapper
                narrow
                buttonSx={buttonSx}
                open={openComp}
                setOpen={setOpenComp}
                endIcon={<FontAwesomeIcon name="calendar-day" />}
                renderLabel={() => titleComp}
              >
                <MonthYearMenu
                  onChange={handleComparisonChange}
                  defaultValue={state.comparison_month}
                  minDate={minMonthYear}
                  maxDate={maxMonthYear}
                />
              </StlPopoverWrapper>
            </Stack>
          </Box>
        </Stack>
      </StlPopup>
      {!isEditMode && (
        <ConfirmCreatePopup
          itemName="Special Event"
          isOpen={openConfirm}
          onApply={handleApply}
          onClose={() => {
            onClose();
            setOpenConfirm(false);
          }}
          onBack={() => {
            setOpenConfirm(false);
          }}
          infoAlert="Special event dates may take a few hours to a few days to load. Please review details before confirming."
          confirmBody={
            <Stack className={styles.popoverInner}>
              <Box className={styles.confirmRow}>
                <Typography className={styles.confirmTitle}>
                  Date(s) of Event
                </Typography>
                <Box className={styles.confirmData}>
                  <Typography variant="body2">{getButtonTitle()}</Typography>
                </Box>
              </Box>
              <Box className={styles.confirmRow}>
                <Typography className={styles.confirmTitle}>
                  Event Name
                </Typography>
                <Box className={styles.confirmData}>
                  <Typography variant="body2">{state.name}</Typography>
                </Box>
              </Box>
              <Box className={styles.confirmRow}>
                <Typography className={styles.confirmTitle}>
                  Comparison Month
                </Typography>
                <Box className={styles.confirmData}>
                  <Typography variant="body2">{titleComp}</Typography>
                </Box>
              </Box>
            </Stack>
          }
        />
      )}
    </>
  );
}

function MaxDaysInfo({ days }: { days: number }) {
  return (
    <Box>
      <Typography variant="caption">
        You can select up to
        <Box component="span" className={styles.bgBox}>
          {days} days
        </Box>
        based on your quota.
      </Typography>
    </Box>
  );
}

export function ComparisonDesc({ numb }: { numb: number }) {
  const { isNew, isEditable } = useUprStatus();
  const project = useAppSelector(selectProject);
  const { data: typicalMonthsInfo } = useGetTypicalMonthsQuery(project);

  const isUpr = isNew || isEditable;

  const typicalMonthsList: Array<string> = useMemo(() => {
    if (typicalMonthsInfo) {
      return sortTypicalMonths(typicalMonthsInfo).map(
        (info) => info.typicalMonth,
      );
    } else {
      return [];
    }
  }, [typicalMonthsInfo]);

  const rollingRange = useMemo(() => {
    if (isUpr || typicalMonthsList.length === 0) return null;
    // the most recent month
    const end = dayjs(typicalMonthsList.at(-1), 'MMMM-YYYY');
    if (!end.isValid()) return null;
    const start = end.subtract(numb - 1, 'month');
    return `${start.format('MMM YYYY')} - ${end.format('MMM YYYY')}`;
  }, [numb, isUpr, typicalMonthsList]);

  return (
    <Typography sx={{ minHeight: !isUpr ? 54 : 'unset' }}>
      {isUpr
        ? `Once your project is active,
it will include a rolling window of the most recent ${numb} months to use for comparisons.`
        : `This project includes a rolling ${numb} month window (currently ${rollingRange}) to use
        for comparisons.`}{' '}
      <Link
        aria-label="Learn more"
        id="learn-more-comp"
        target="_blank"
        rel="noopener noreferrer"
        href="/"
        color="primary"
        underline="always"
      >
        Learn more
      </Link>
    </Typography>
  );
}
